import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chip } from '@c2fo/liquidity';
import { LoadingOverlay } from '../LoadingSpinner';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardHeader } from '../CardHeader';
import { DetailField } from '../DetailField';
import { EditButton } from '../EditButton';
import { FieldsContainer } from '../FieldsContainer';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { useCredentialsState } from '../../contexts';
import { CredentialTypeChip } from '../CredentialTypeChip';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const CredentialView: React.FC = () => {
  const { credentialUid } = useParams<{ credentialUid: string }>();
  const navigate = useNavigate();
  const {
    currentCredential: credential,
    isLoadingCredential: isLoading,
    fetchCredential,
    clearCurrentCredential,
  } = useCredentialsState();

  useEffect(() => {
    if (credentialUid) {
      // Construct full path: credentials/{credentialUid}
      fetchCredential(`credentials/${credentialUid}`);
    }

    return () => {
      clearCurrentCredential();
    };
  }, [credentialUid, fetchCredential, clearCurrentCredential]);

  if (isLoading) {
    return <LoadingOverlay message="Loading credential..." />;
  }

  if (!credential) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/credentials" label="Back to Credentials" />
        <NotFoundCard message="Credential not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to="/file-delivery/credentials" label="Back to Credentials" />

      <Card>
        <CardHeader
          chip={<CredentialTypeChip credentialType={credential.credentialType} />}
          title={credential.displayName}
          actions={<EditButton onClick={() => navigate(`/file-delivery/credentials/${credentialUid}/edit`)} />}
        />
        <FieldsContainer>
          <DetailField label="Display Name" value={credential.displayName} />
          {credential.maskedValues && Object.keys(credential.maskedValues).length > 0 && (
            <DetailField
              label="Stored Values"
              value={
                <div className="flex flex-wrap gap-1">
                  {Object.keys(credential.maskedValues).map((key) => (
                    <Chip key={key} label={key} type="secondary" variant="outlined" size="xs" />
                  ))}
                </div>
              }
            />
          )}
          <DetailField label="UID" value={credential.uid} mono />
          <DetailField label="Resource Name" value={credential.name} mono />
          <DetailField
            label="Created"
            value={credential.createTime ? formatDateWithTimezone(credential.createTime) : undefined}
          />
          <DetailField
            label="Last Updated"
            value={credential.updateTime ? formatDateWithTimezone(credential.updateTime) : undefined}
          />
        </FieldsContainer>
      </Card>
    </PageContainer>
  );
};
